import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// NV8 — the Actor-level unseen-settle seen cursor through the local server's
// grant door: read rides the same /view-prefs row read RAIL-6 wears, stamp is
// its own door ("the Actor stood here now"). The browser names a World; the
// Actor arrives from the deposited grant, never from here. Every refusal is
// typed and the caller's law is the same everywhere: on refusal the
// device-local cursor carries alone.

export type WorldSeenCursorOutcome =
  | { readonly ok: true; readonly seenCursorAt: number | null }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

async function call(
  path: string,
  worldKey: string,
  fetchImpl: typeof fetch,
): Promise<WorldSeenCursorOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, { worldKey }, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) {
    return {
      ok: false,
      kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
      reason:
        typeof body?.["reason"] === "string"
          ? body["reason"]
          : "The Actor-level seen cursor is unavailable.",
    };
  }
  const seenCursorAt = body["seenCursorAt"];
  if (
    seenCursorAt !== null &&
    !(typeof seenCursorAt === "number" && Number.isFinite(seenCursorAt))
  ) {
    return {
      ok: false,
      kind: "transport",
      reason: "The local Cleo server returned an invalid seen cursor.",
    };
  }
  return { ok: true, seenCursorAt: seenCursorAt as number | null };
}

/** Read this Actor's seen cursor for one World; null means never stamped. */
export async function readWorldSeenCursor(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldSeenCursorOutcome> {
  return call("/api/neucleos/world/view-prefs", worldKey, fetchImpl);
}

/** Stamp this Actor's seen cursor to now in the owning World's viewPrefs row. */
export async function stampWorldSeenCursor(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldSeenCursorOutcome> {
  return call("/api/neucleos/world/view-prefs/seen", worldKey, fetchImpl);
}
